import { useIdentity } from '@vp/auth-react';
import { useCallback, useContext, useEffect, useState, useRef } from 'react';
import { IApiHooksWithTriggerDataAndCancel, IIdentityObject } from '../../interfaces/app-interfaces';
import { ICustomFlow, IMessage } from '../../interfaces/component-interfaces';
import { ICBBaseResponse } from '../../interfaces/chatbot-api-interfaces';
import { ChatbotContext } from '../../contexts/ChatbotContext';
import { showAgentButton, transformChatbotResponse } from '../../utils/chatbot/order-tracking/response-handlers';
import {
  handleMessageWithDifferentQuery,
  handleNonLoggedInOrderMessage,
  handleOrderSelect,
  handleProductSelect,
  handleTextMessage,
} from '../../utils/chatbot/order-tracking/request-handlers';
import { useCustomTracking } from '../custom-hooks/useTracking';
import { API_ENDPOINTS } from '../../config/api-config';
import {
  getChatbotEnv,
  getChatbotToken,
  getRestId,
  setChatbotToken,
  setOrderNumberForChatbot,
  setSelectedProductIdsForChatbot,
} from '../../utils/session-storage-utils';
import { useNRLogging } from '../custom-hooks/useNRLogging';
import { IOrderData } from '../../interfaces/order-tracking-flow-interfaces';
import { CAContext } from '../../contexts/CAContext';
import { getFlowInfo } from '../../utils/chatbot/order-tracking/order-flow-utils';
import { getChatbotSessionInfo } from '../../utils/chatbot/api-utils';

export interface ISendMessage {
  type: 'order_select' | 'product_select' | 'text_message' | 'non_logged_in_order_tracking' | 'text_message_with_different_query';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  messageData: any;
}

export interface INonLoggedInMessageData {
  messageText: string;
  query: string;
  email: string;
}

// Cache to store the number of times 501 exception occurred
let exception501Count = 0;

const useCBChatMessage = (): IApiHooksWithTriggerDataAndCancel<{ messages: IMessage[]; flowType?: ICustomFlow }, ISendMessage> & {
  cancel: () => void;
} => {
  const { logCustomAttributesNR } = useNRLogging();

  const {
    chatState: { locale, availableSchedule, gcpChatData, isProductAgentEnabled },
    setChatState,
  } = useContext(CAContext);

  const { setState: setChatbotState } = useContext(ChatbotContext);
  const { sendTrackingEvent } = useCustomTracking();
  const identity = useIdentity();
  const [data, setData] = useState<{ messages: IMessage[]; flowType?: ICustomFlow }>({ messages: [], flowType: undefined });
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | undefined>();
  const [requestData, setRequestData] = useState<ISendMessage>();
  const [query, setQuery] = useState<string>();
  const [userMessageData, setUserMessageData] = useState<ISendMessage | undefined>();

  // Create a reference to an AbortController that will allow us to cancel requests
  const abortControllerRef = useRef<AbortController | null>(null);

  // Function to cancel the current API request
  const cancel = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      setLoading(false);
    }
  }, [setChatbotState]);

  const trigger = useCallback(
    (msgData: ISendMessage) => {
      // Cancel any existing request before starting a new one
      cancel();

      console.log('trigger', msgData);
      sendTrackingEvent('Chatbot Clicked', 'Chatbot:Message', 'chatUserMessage');
      switch (msgData.type) {
        case 'order_select': {
          const { query: orderQuery, userMessage: orderMessage } = handleOrderSelect(msgData);
          setQuery(orderQuery);
          setUserMessageData(msgData);
          setChatbotState((prev) => {
            const updateConversationMessages = prev.conversationMessages.map((message) => {
              // Remove Order List from the Conversation after user has selected order to prevent multiple clicks
              if (message.customMessageType === 'orderListMessage') {
                return { ...message, additionalComponents: undefined };
              }
              return message;
            });
            return {
              ...prev,
              conversationMessages: [...updateConversationMessages, orderMessage],
              orderNumber: (msgData.messageData as IOrderData).orderNumber,
              latestQuery: orderQuery,
            };
          });
          setOrderNumberForChatbot((msgData.messageData as IOrderData).orderNumber);
          break;
        }
        case 'product_select': {
          const { query: productQuery, userMessage: productMessage, selectedProductIds } = handleProductSelect(msgData);
          setQuery(productQuery);
          setUserMessageData(msgData);
          setChatbotState((prev) => {
            const updateConversationMessages = prev.conversationMessages.map((message) => {
              // Remove Order List from the Conversation after user has selected order to prevent multiple clicks
              if (message.customMessageType === 'productListMessage') {
                return { ...message, additionalComponents: undefined };
              }
              return message;
            });
            return { ...prev, conversationMessages: [...updateConversationMessages, productMessage], selectedProductIds, latestQuery: productQuery };
          });
          setSelectedProductIdsForChatbot(selectedProductIds);
          break;
        }
        case 'non_logged_in_order_tracking': {
          const { userMessage, query } = handleNonLoggedInOrderMessage(msgData);
          setQuery(query);
          setUserMessageData(msgData);
          setChatbotState((prev) => {
            const updateConversationMessages = prev.conversationMessages.map((message, index) => {
              if (index === prev.conversationMessages.length - 1) {
                // Remove Order Form from the Conversation after user has selected order to prevent multiple clicks
                return { ...message, additionalComponents: undefined };
              }
              return message;
            });
            return {
              ...prev,
              conversationMessages: [...updateConversationMessages, userMessage],
              latestQuery: query,
            };
          });
          break;
        }
        case 'text_message_with_different_query': {
          const { query: textQuery, userMessage: textMessage } = handleMessageWithDifferentQuery(msgData);
          setQuery(textQuery);
          setUserMessageData(msgData);
          setChatbotState((prev) => {
            const updateConversationMessages = prev.conversationMessages.map((message, index) => {
              if (index === prev.conversationMessages.length - 1) {
                return { ...message, additionalComponents: undefined };
              }
              return message;
            });
            return { ...prev, conversationMessages: [...updateConversationMessages, textMessage], latestQuery: textQuery };
          });
          break;
        }
        case 'text_message':
        default: {
          const { query: textQuery, userMessage: textMessage } = handleTextMessage(msgData);
          setUserMessageData(msgData);
          setQuery(textQuery);
          setChatbotState((prev) => {
            const updateConversationMessages = prev.conversationMessages.map((message, index) => {
              if (index === prev.conversationMessages.length - 1) {
                // Remove any custom components from the Conversation after user has selected an option to prevent multiple clicks
                return { ...message, additionalComponents: undefined };
              }
              return message;
            });
            return { ...prev, conversationMessages: [...updateConversationMessages, textMessage], latestQuery: textQuery };
          });
          break;
        }
      }
      setRequestData(msgData);
    },
    [cancel, sendTrackingEvent, setChatbotState],
  );

  useEffect(() => {
    const fetchData = async () => {
      if (requestData) {
        setLoading(true);
        setChatbotState((prev) => ({
          ...prev,
          showHelperButton: false,
        }));

        // Create a new AbortController for this request
        abortControllerRef.current = new AbortController();
        const signal = abortControllerRef.current.signal;

        try {
          const chatbotToken = getChatbotToken();
          const { authorizationHeader, canonicalId, isSignedIn } = identity as IIdentityObject;
          const myHeaders = new Headers();
          myHeaders.append('Authorization', authorizationHeader || '');
          myHeaders.append('Content-Type', 'application/json');
          myHeaders.append('Chatbot-Authorization', `Bearer ${chatbotToken}`);

          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const requestBody: Record<string, any> = {
            query,
            content_json: userMessageData,
            shopper_id: canonicalId || '',
            request_id: getRestId() || '',
            chatLocale: locale,
            agentChatInfo: {
              isChatEnabled: gcpChatData?.status.isChatEnabled,
              isEmodeEnabled: gcpChatData?.status.isEmodeEnabled || false,
              nextAvailableSchedule: availableSchedule,
            },
            guest_user: isSignedIn ? 'false' : 'true',
            isProductAgentEnabled: isProductAgentEnabled || false,
          };
          if (getChatbotEnv() !== 'prod') {
            requestBody.promptData = window.location.search;
          }
          const guestUserEmail =
            (requestData.type === 'non_logged_in_order_tracking' && (requestData.messageData as INonLoggedInMessageData).email) || undefined;
          // Only add guest_user_email if it's defined
          if (guestUserEmail) {
            requestBody.guest_user_email = guestUserEmail;
          }

          const requestOptions = {
            method: 'POST',
            headers: myHeaders,
            body: JSON.stringify(requestBody),
            redirect: 'follow' as RequestRedirect,
            signal, // Add the AbortController signal to the request
          };

          logCustomAttributesNR('ca-chatbot-api-request', {
            logLevel: 'info',
            message: 'Sending message to chatbot',
            type: requestData.type,
            apiType: 'sendChatbotMessage',
            chatStatus: requestBody?.agentChatInfo?.isChatEnabled,
            isEmodeEnabled: requestBody?.agentChatInfo?.isEmodeEnabled || false,
          });
          const startTime = Date.now();
          fetch(`${API_ENDPOINTS.CHATBOT_BASE_URL()}/chat`, requestOptions)
            .then((response) => {
              let throwError = false;
              if (!response.ok) {
                throwError = true;
                if (response.status === 401) {
                  setChatbotState((prev) => ({ ...prev, isSessionExpired: true }));
                  /** Set endchatinfo as chatbot so that we can show the feedback */
                  setChatState((prev) => ({
                    ...prev,
                    endChatInfo: {
                      sessionType: 'chatbot',
                      sessionId: getChatbotSessionInfo(chatbotToken || undefined)?.sessionId || '',
                    },
                  }));
                }

                if (response.status === 500) {
                  return response.json().then((errorData) => {
                    const errorResponse = errorData.data as ICBBaseResponse;
                    if (errorResponse.response.response === 'Error' && errorResponse.response.error === 'Something went wrong.') {
                      throwError = false;
                      return errorResponse;
                    }
                  });
                }
                if (response.status === 504 && isProductAgentEnabled) {
                  throwError = false;
                  return { response: { error: 'Gateway Timeout', response: 'Error' } as ICBBaseResponse };
                }
                if (throwError) {
                  // If response is not ok (status not in the range 200-299),
                  // throw an error to skip the next then block
                  const error = new Error(`API responded with status: ${response.status}`);
                  error.name = 'ApiError';
                  throw error;
                }
              }
              return response.json();
            })
            .then((result) => {
              if (result) {
                const cbResult = result as ICBBaseResponse;
                if (cbResult?.response?.response === 'Error' && cbResult?.response?.error === 'Gateway Timeout') {
                  logCustomAttributesNR('ca-chatbot-api-response', {
                    logLevel: 'info',
                    message: 'Chatbot API Gateway Timeout error',
                    apiType: 'sendChatbotMessage',
                  });
                  exception501Count = 2;
                  const msg = transformChatbotResponse(
                    locale,
                    cbResult,
                    getRestId(false),
                    new Date().getTime(),
                    false,
                    false,
                    exception501Count,
                    gcpChatData?.status.isEmodeEnabled || !gcpChatData?.status.isChatEnabled || false,
                  );
                  setData({ messages: msg, flowType: undefined });
                  setError(undefined);
                  if (exception501Count >= 2) {
                    exception501Count = 0;
                  }
                } else if (cbResult?.response?.response === 'Error' && cbResult?.response?.error === 'Something went wrong.') {
                  logCustomAttributesNR('ca-chatbot-api-response', {
                    logLevel: 'info',
                    message: 'Chatbot session API returned 500 error for global exception',
                    apiType: 'sendChatbotMessage',
                  });
                  exception501Count += 1;
                  const msg = transformChatbotResponse(
                    locale,
                    cbResult,
                    getRestId(false),
                    new Date().getTime(),
                    false,
                    false,
                    exception501Count,
                    gcpChatData?.status.isEmodeEnabled || !gcpChatData?.status.isChatEnabled || false,
                  );
                  setData({ messages: msg, flowType: undefined });
                  setError(undefined);
                  if (exception501Count >= 2) {
                    exception501Count = 0;
                  }
                } else {
                  logCustomAttributesNR('ca-chatbot-api-response', {
                    logLevel: 'info',
                    message: 'Ending chatbot session API success',
                    apiType: 'sendChatbotMessage',
                  });
                  const msg = transformChatbotResponse(
                    locale,
                    cbResult,
                    getRestId(false),
                    undefined,
                    undefined,
                    undefined,
                    undefined,
                    gcpChatData?.status.isEmodeEnabled || !gcpChatData?.status.isChatEnabled || false,
                  );
                  let showButton = false;
                  msg.forEach((message) => {
                    const msgContent = message.messageContent;
                    if (Array.isArray(msgContent)) {
                      msgContent.forEach((content) => {
                        showButton = showAgentButton(content);
                      });
                    }
                  });
                  const flowInfo = getFlowInfo(cbResult?.response?.data?.flow_name || '', showButton);

                  /** If refreshed_token is present, update the chatbot token */
                  if (cbResult?.refreshed_token) {
                    setChatbotToken(cbResult.refreshed_token, isSignedIn);
                  }

                  setData({ messages: msg, flowType: flowInfo });
                  setError(undefined);
                }
              }
            })
            .catch((error) => {
              const endTime = Date.now();
              const duration = endTime - startTime;
              // Only set error if it's not an abort error
              if (error.name !== 'AbortError') {
                if (error.name === 'TypeError' && error.message?.includes('Failed to fetch') && duration >= 29000) {
                  console.error('Network error detected (likely 504 Gateway Timeout):', error);
                  logCustomAttributesNR('ca-chatbot-api-response', {
                    logLevel: 'info',
                    message: 'Chatbot API Gateway Timeout error',
                    apiType: 'sendChatbotMessage',
                  });
                  exception501Count = 2;
                  const cbResult: ICBBaseResponse = {
                    response: {
                      response: 'Error',
                      error: 'Gateway Timeout',
                    },
                  };
                  const msg = transformChatbotResponse(
                    locale,
                    cbResult,
                    getRestId(false),
                    new Date().getTime(),
                    false,
                    false,
                    exception501Count,
                    gcpChatData?.status.isEmodeEnabled || !gcpChatData?.status.isChatEnabled || false,
                  );
                  setData({ messages: msg, flowType: undefined });
                  setError(undefined);
                  if (exception501Count >= 2) {
                    exception501Count = 0;
                  }
                } else {
                  setError(error as Error);
                  console.error(error);
                  logCustomAttributesNR('ca-chatbot-api-error', {
                    errorName: error.name,
                    errorStack: error.stack,
                    message: error.message,
                    logLevel: 'error',
                    apiType: 'sendChatbotMessage',
                  });
                }
              }
            })
            .finally(() => {
              // Only update loading state if the request wasn't aborted
              if (!signal.aborted) {
                setLoading(false);
              }
              setRequestData(undefined); // Reset requestData after completion
            });
        } catch (err) {
          // Check if the error is due to the abort operation
          if (err && (err as Error).name !== 'AbortError') {
            setError(err as Error);
          }
          console.error(err);
          logCustomAttributesNR('ca-chatbot-api-error', {
            errorName: err.name,
            errorStack: err.stack,
            message: err.message,
            logLevel: 'error',
            apiType: 'sendChatbotMessage',
          });
        }
      }
    };

    fetchData();

    // Clean up function to abort any pending requests when the component unmounts
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [requestData, gcpChatData, availableSchedule, locale]);

  return {
    data,
    loading,
    error,
    trigger,
    cancel, // Expose the cancel function in the hook's return value
  };
};

export { useCBChatMessage };
